import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { showAlert } from 'actions/app';
import { getSmartTags } from 'services/bulkEmail';
import strings from 'strings';
import { FeatureFlags } from 'UI/constants/featureFlags';

import { BulkDrawerFormFieldsMap } from '../BulkEmailForm/fields';

import { getBulkEmailCustomValidations } from './utils';

const {
  forms: { validations: validationsCopies, signatureInfo }
} = strings.bulkEmails;

/**
 * @typedef {Object} FieldConfig
 * @property {string} key
 * @property {string} requiredMessage
 * @property {string} fieldName
 */

/**
 * @typedef {Object} RegistrationConfig
 * @property {FieldConfig} subject - Configuration for the subject field registration.
 * @property {FieldConfig} emailBody - Configuration for the email body field registration.
 */

/**
 * Custom hook to fetch Smart Tags and initialize bulk email form fields.
 * @param {Object} props
 * @param {Function} props.getValues - react-hook-form getValues.
 * @param {Function} props.register - react-hook-form register.
 * @param {Function} props.setValue - react-hook-form setValue.
 * @param {RegistrationConfig} props.registrationConfig - Field and validation setup.
 * @param {function({loading: boolean}): void} [props.onFetchingSmartTags] - Loading state callback.
 */
export const useBulkSmartTags = ({
  getValues,
  register,
  registrationConfig,
  setValue,
  onFetchingSmartTags = () => {}
}) => {
  const dispatch = useDispatch();
  const hasSuccessfullyLoaded = useRef(false);

  useEffect(() => {
    if (hasSuccessfullyLoaded.current) return;

    onFetchingSmartTags({ loading: true });
    (async () => {
      hasSuccessfullyLoaded.current = true;

      const smartTags = getValues(BulkDrawerFormFieldsMap.SmartTags.key);
      const response = smartTags
        ? {
            success: true,
            data: smartTags
          }
        : await getSmartTags();
      if (response.success) {
        register({
          name: BulkDrawerFormFieldsMap.SmartTags.key,
          value: [...response.data, signatureInfo.selectOption]
        });
        register(
          { name: registrationConfig.subject.key, value: '' },
          {
            required:
              registrationConfig.subject.requiredMessage || validationsCopies.subject.required,
            ...getBulkEmailCustomValidations({
              fieldName: registrationConfig.subject.fieldName,
              featureFlag: FeatureFlags.BulkSmartTagOnSubject,
              smartTags: response.data
            })
          }
        );
        register(
          { name: registrationConfig.emailBody.key || BulkDrawerFormFieldsMap.EmailBody.key },
          {
            required:
              registrationConfig.emailBody.requiredMessage || validationsCopies.emailBody.required,
            ...getBulkEmailCustomValidations({
              fieldName: registrationConfig.emailBody.fieldName,
              featureFlag: FeatureFlags.BulkSmartTagOnEmailBody,
              smartTags: response.data
            })
          }
        );
      } else {
        hasSuccessfullyLoaded.current = false;
        dispatch(showAlert(response.alert));
      }

      onFetchingSmartTags({ loading: false });
    })();
  }, [dispatch, getValues, onFetchingSmartTags, register, registrationConfig, setValue]);
};
